/**
 * The JavaScript half of reduced motion — requirement 6.6.
 *
 * The CSS half is in the generated token stylesheet: a `prefers-reduced-motion: reduce` query that
 * collapses all four duration tokens to `1ms` and pins `transition-property` to `opacity`, so
 * anything driven by a CSS transition is handled without asking. This is for what that query cannot
 * reach — a spring, a `requestAnimationFrame` loop, an autoplaying sequence — where the component has
 * to know the preference and choose a different behaviour rather than a shorter one.
 *
 * The two halves read the same media query, so they cannot disagree.
 *
 * ## Why this is written here rather than imported
 *
 * The design mentions "the motion library's reduced-motion hook", and a motion library will arrive
 * with the spring-driven affordances at Task 14. Adding it now would put an unused dependency in the
 * tree, which is exactly what `depcheck` exists to reject at Task 16 — the old site ships `passport`,
 * `express-session`, and three more with no code that imports them. This hook is four lines against a
 * platform API and is the seam the library plugs into when there is something for it to animate.
 */
import { useEffect, useState } from 'react';
export const reducedMotionQuery = '(prefers-reduced-motion: reduce)';
/**
 * Reads the preference and stays current if the user changes it mid-session.
 *
 * Defaults to `false` when `matchMedia` is unavailable, which is the honest default: no preference
 * expressed is not the same as a preference for less motion, and suppressing animation for everyone
 * on an older browser is a change nobody asked for.
 */
export const useReducedMotion = () => {
    // Read in the initialiser, not in the effect. An effect that calls `setState` in its body triggers a
    // second render, and the first one would have animated — which for a user who asked for less motion
    // is the one render that matters. React's own lint rule says the same thing.
    const [reduced, setReduced] = useState(() => globalThis.matchMedia?.(reducedMotionQuery).matches ?? false);
    useEffect(() => {
        const query = globalThis.matchMedia?.(reducedMotionQuery);
        if (!query)
            return;
        const update = (event) => {
            setReduced(event.matches);
        };
        query.addEventListener('change', update);
        return () => {
            query.removeEventListener('change', update);
        };
    }, []);
    return reduced;
};
//# sourceMappingURL=useReducedMotion.js.map